import AddIcon from "@mui/icons-material/Add";
import { Box, Button, IconButton, styled, Tooltip } from "@mui/material";
import { useState } from "react";
import { toast } from "sonner";

import type { CountryCodes, LanguageCodes } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useCreateProjectMutation } from "Projects/Api/Mutations/useCreateProjectMutation";
import { CreateProjectModal } from "Projects/Components/CreateProjectModal";
import type { CreateProjectFormData } from "Projects/Forms/createProjectFormSchema";
import { dateToApiString } from "Shared/Utils/Format/date";

const IconButtonWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  border: 1px solid ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.main};
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  padding: 0;

  &:hover {
    background-color: ${(props) => props.theme.palette.primary.main}10;
    border-color: ${(props) => props.theme.palette.primary.main};
  }
`;

type NewProjectButtonProps = {
  isCollapsed: boolean;
};

export const NewProjectButton = ({
  isCollapsed,
}: NewProjectButtonProps): React.ReactNode => {
  const { t } = useResource();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const createProjectMutation = useCreateProjectMutation();

  const handleCreateProject = async (projectData: CreateProjectFormData) => {
    await createProjectMutation.mutateAsync({
      name: projectData.name,
      clientID: projectData.clientID,
      moduleID: projectData.moduleID,
      industry: projectData.industry,
      language: projectData.language as LanguageCodes,
      licensedCountries: projectData.licensedCountries as CountryCodes[],
      jobsLimit: projectData.jobsLimit,
      startDate: dateToApiString(projectData.startDate),
      endDate: dateToApiString(projectData.endDate),
      exclusiveContracts: projectData.hasExclusiveContracts
        ? projectData.exclusiveContracts.map((ec) => ({
            actorID: ec.actorID,
            price: ec.price,
            jobLimit: ec.jobLimit,
          }))
        : [],
    });

    toast.success(t(Resources.Projects.CreateProjectModal.SuccessMessage));
    setIsModalOpen(false);
  };

  if (isCollapsed) {
    return (
      <>
        <IconButtonWrapper>
          <Tooltip title={t(Resources.Common.NewProject)} placement="right">
            <StyledIconButton onClick={() => setIsModalOpen(true)}>
              <AddIcon />
            </StyledIconButton>
          </Tooltip>
        </IconButtonWrapper>
        <CreateProjectModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleCreateProject}
          serverError={createProjectMutation.error}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        fullWidth
        onClick={() => setIsModalOpen(true)}
        sx={{ px: Spacing.L }}
      >
        {t(Resources.Common.NewProject)}
      </Button>
      <CreateProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleCreateProject}
        serverError={createProjectMutation.error}
      />
    </>
  );
};
